import { apiSlice } from './apiSlice'

export enum DocumentType {
  CASE_DOCUMENT = 'case_document',
  REFERENCE = 'reference',
  MEMORIAL = 'memorial',
  EVIDENCE = 'evidence',
  TRANSCRIPT = 'transcript',
  RUBRIC = 'rubric',
}

export enum DocumentStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  FAILED = 'failed',
}

export interface Document {
  id: string
  title: string
  description?: string
  document_type: DocumentType
  case_id?: string
  team_id?: string
  uploaded_by: string
  storage_path: string
  file_name: string
  file_size: number
  mime_type: string
  status: DocumentStatus
  chunk_count: number
  embedding_model?: string
  processed_at?: string
  created_at?: string
  updated_at?: string
}

export interface DocumentCreate {
  title: string
  description?: string
  document_type: DocumentType
  case_id?: string
  team_id?: string
}

export interface DocumentSearchQuery {
  query: string
  case_id?: string
  team_id?: string
  document_type?: DocumentType
  limit?: number
  similarity_threshold?: number
}

/** Body cho POST /documents/search/scoring (RAG hỗ trợ chấm điểm) */
export interface DocumentSearchScoringQuery {
  query: string
  case_id: string
  team_id: string
  limit?: number
  similarity_threshold?: number
  case_weight?: number
  team_weight?: number
}

export interface DocumentSearchResult {
  chunk_id: string
  document_id: string
  document_title: string
  content: string
  similarity_score: number
  metadata?: Record<string, any>
}

export const documentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    uploadDocument: builder.mutation<Document, FormData>({
      query: (formData) => ({
        url: '/documents/create',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Document'],
    }),
    getDocuments: builder.query<Document[], { case_id?: string; team_id?: string; document_type?: string }>({
      query: (params) => ({
        url: '/documents/list',
        params,
      }),
      providesTags: ['Document'],
    }),
    getDocument: builder.query<Document, string>({
      query: (id) => `/documents/${id}`,
      providesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
    downloadDocument: builder.query<Blob, string>({
      query: (id) => ({
        url: `/documents/${id}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    processDocument: builder.mutation<{ message: string; status: string }, string>({
      query: (id) => ({
        url: `/documents/${id}/process`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
    searchDocuments: builder.mutation<DocumentSearchResult[], DocumentSearchQuery>({
      query: (body) => ({
        url: '/documents/search',
        method: 'POST',
        body,
      }),
    }),
    /** RAG tìm kiếm hỗ trợ chấm điểm (case + team submissions, kết hợp case_weight/team_weight) */
    searchDocumentsScoring: builder.mutation<DocumentSearchResult[], DocumentSearchScoringQuery>({
      query: (body) => ({
        url: '/documents/search/scoring',
        method: 'POST',
        body,
      }),
    }),
    deleteDocument: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
  }),
})

export const {
  useUploadDocumentMutation,
  useGetDocumentsQuery,
  useGetDocumentQuery,
  useLazyDownloadDocumentQuery,
  useProcessDocumentMutation,
  useSearchDocumentsMutation,
  useSearchDocumentsScoringMutation,
  useDeleteDocumentMutation,
} = documentsApi
